<template>
  <div class="card rb-retention-card p-3 mt-3">
    <div v-if="processedData.length > 0" class="table-responsive">
      <table class="table align-items-center mb-0 rb-retention-table">
        <thead>
          <tr>
            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 50px; border-bottom: none !important;"></th>
            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 150px; border-bottom: none !important;">
              {{ isUtmBreakdown ? $t('ReportBuilder.retention.utmLabel') : $t('ReportBuilder.retention.cohort') }}
            </th>
            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center" style="border-bottom: none !important;">{{ $t('ReportBuilder.retention.size') }}</th>
            <th
              v-for="i in maxIntervals"
              :key="i"
              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
              style="border-bottom: none !important;"
            >
              {{ $t('ReportBuilder.constructor.' + granularity, { n: i - 1 }) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Mean Row -->
          <tr v-if="meanRow" class="mean-row">
            <td class="align-middle text-center p-0">
              <div class="d-flex align-items-center justify-content-center gap-2 h-100">
                <el-checkbox
                  :model-value="!hiddenDatasets.includes(meanVisibilityKey())"
                  @change="toggleVisibility(meanVisibilityKey())"
                  class="mb-0"
                />
                <el-popover
                  placement="bottom"
                  :width="228"
                  trigger="click"
                  popper-class="rb-style-popover"
                >
                  <template #reference>
                    <div class="series-color-indicator cursor-pointer" :style="{ backgroundColor: palette[0] }"></div>
                  </template>
                  <div class="style-picker">
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleWidth') }}</div>
                      <div class="style-btn-row">
                        <button v-for="w in lineWidths" :key="w.value" class="style-btn"
                          :class="{ active: getCohortLineStyle('__mean__').lineWidth === w.value }"
                          @click="updateCohortStyle('__mean__', { lineWidth: w.value })">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" :stroke-width="w.value" stroke-linecap="round"/></svg>
                        </button>
                      </div>
                    </div>
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleType') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineStyle === 'solid' }" @click="updateCohortStyle('__mean__', { lineStyle: 'solid' })" :title="$t('ReportBuilder.table.styleSolid')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineStyle === 'dashed' }" @click="updateCohortStyle('__mean__', { lineStyle: 'dashed' })" :title="$t('ReportBuilder.table.styleDashed')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 4"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineStyle === 'dotted' }" @click="updateCohortStyle('__mean__', { lineStyle: 'dotted' })" :title="$t('ReportBuilder.table.styleDotted')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="2 4"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineStyle === 'dashdot' }" @click="updateCohortStyle('__mean__', { lineStyle: 'dashdot' })" :title="$t('ReportBuilder.table.styleDashdot')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 3 2 3"/></svg>
                        </button>
                      </div>
                    </div>
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleTension') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineTension === 'straight' }" @click="updateCohortStyle('__mean__', { lineTension: 'straight' })" :title="$t('ReportBuilder.table.styleStraight')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><polyline points="3,15 12,5 24,15 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').lineTension !== 'straight' }" @click="updateCohortStyle('__mean__', { lineTension: 'smooth' })" :title="$t('ReportBuilder.table.styleSmooth')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><path d="M3,15 C9,15 9,5 18,10 C27,15 27,5 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>
                        </button>
                      </div>
                    </div>
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.stylePoints') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: !getCohortLineStyle('__mean__').showPoints }" @click="updateCohortStyle('__mean__', { showPoints: false })" :title="$t('ReportBuilder.table.styleNoPoints')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle('__mean__').showPoints }" @click="updateCohortStyle('__mean__', { showPoints: true })" :title="$t('ReportBuilder.table.styleWithPoints')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="10" r="2.5" fill="currentColor"/><circle cx="18" cy="10" r="2.5" fill="currentColor"/><circle cx="26" cy="10" r="2.5" fill="currentColor"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </el-popover>
              </div>
            </td>
            <td class="text-sm font-weight-bold">
              <i class="fas fa-chart-line me-2 text-primary"></i> {{ $t('ReportBuilder.retention.mean') }}
            </td>
            <td class="align-middle text-center text-sm font-weight-bold">
              {{ meanRow.size }}
            </td>
            <td
              v-for="(val, idx) in meanRow.intervals"
              :key="'mean-'+idx"
              class="align-middle text-center text-sm"
              :style="getCellStyle(val)"
            >
              <el-tooltip v-if="val !== null" :content="cellTooltip(meanRow, idx)" placement="top" :show-after="150" :teleported="true">
                <span :class="{ 'font-weight-bold text-white': val > 80 }">
                  {{ val }}%
                </span>
              </el-tooltip>
            </td>
          </tr>

          <!-- Spacer Row -->
          <tr v-if="meanRow" style="height: 4px;"><td :colspan="maxIntervals + 3" style="padding: 0; border: none;"></td></tr>

          <!-- Cohort Rows -->
          <tr v-for="(row, rIdx) in displayRows" :key="row._key">
            <td class="align-middle text-center p-0">
              <div class="d-flex align-items-center justify-content-center gap-2 h-100">
                <el-checkbox
                  :model-value="!hiddenDatasets.includes(rowVisibilityKey(row, rIdx))"
                  @change="toggleVisibility(rowVisibilityKey(row, rIdx))"
                  class="mb-0"
                />
                <el-popover
                  placement="bottom"
                  :width="228"
                  trigger="click"
                  popper-class="rb-style-popover"
                >
                  <template #reference>
                    <div
                      class="series-color-indicator cursor-pointer"
                      :style="{ backgroundColor: getCohortColor(row, row._paletteIndex ?? rIdx) }"
                    ></div>
                  </template>

                  <div class="style-picker">
                    <!-- Color -->
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleColor') }}</div>
                      <div class="color-palette-grid">
                        <div
                          v-for="color in palette"
                          :key="color"
                          class="color-option"
                          :class="{ active: getCohortColor(row, rIdx) === color }"
                          :style="{ backgroundColor: color }"
                          @click="updateColor(row, color)"
                        ></div>
                      </div>
                    </div>

                    <!-- Line width -->
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleWidth') }}</div>
                      <div class="style-btn-row">
                        <button v-for="w in lineWidths" :key="w.value" class="style-btn"
                          :class="{ active: getCohortLineStyle(styleKey(row)).lineWidth === w.value }"
                          @click="updateCohortStyle(styleKey(row), { lineWidth: w.value })">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" :stroke-width="w.value" stroke-linecap="round"/></svg>
                        </button>
                      </div>
                    </div>

                    <!-- Line style -->
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleType') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineStyle === 'solid' }" @click="updateCohortStyle(styleKey(row), { lineStyle: 'solid' })" :title="$t('ReportBuilder.table.styleSolid')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineStyle === 'dashed' }" @click="updateCohortStyle(styleKey(row), { lineStyle: 'dashed' })" :title="$t('ReportBuilder.table.styleDashed')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 4"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineStyle === 'dotted' }" @click="updateCohortStyle(styleKey(row), { lineStyle: 'dotted' })" :title="$t('ReportBuilder.table.styleDotted')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="2 4"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineStyle === 'dashdot' }" @click="updateCohortStyle(styleKey(row), { lineStyle: 'dashdot' })" :title="$t('ReportBuilder.table.styleDashdot')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="8 3 2 3"/></svg>
                        </button>
                      </div>
                    </div>

                    <!-- Tension -->
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.styleTension') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineTension === 'straight' }" @click="updateCohortStyle(styleKey(row), { lineTension: 'straight' })" :title="$t('ReportBuilder.table.styleStraight')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><polyline points="3,15 12,5 24,15 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).lineTension !== 'straight' }" @click="updateCohortStyle(styleKey(row), { lineTension: 'smooth' })" :title="$t('ReportBuilder.table.styleSmooth')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><path d="M3,15 C9,15 9,5 18,10 C27,15 27,5 33,5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>
                        </button>
                      </div>
                    </div>

                    <!-- Points -->
                    <div class="style-section">
                      <div class="style-label">{{ $t('ReportBuilder.table.stylePoints') }}</div>
                      <div class="style-btn-row">
                        <button class="style-btn" :class="{ active: !getCohortLineStyle(styleKey(row)).showPoints }" @click="updateCohortStyle(styleKey(row), { showPoints: false })" :title="$t('ReportBuilder.table.styleNoPoints')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                        </button>
                        <button class="style-btn" :class="{ active: getCohortLineStyle(styleKey(row)).showPoints }" @click="updateCohortStyle(styleKey(row), { showPoints: true })" :title="$t('ReportBuilder.table.styleWithPoints')">
                          <svg width="36" height="20" viewBox="0 0 36 20"><line x1="3" y1="10" x2="33" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="10" r="2.5" fill="currentColor"/><circle cx="18" cy="10" r="2.5" fill="currentColor"/><circle cx="26" cy="10" r="2.5" fill="currentColor"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </el-popover>
              </div>
            </td>
            <td class="align-middle py-0">
              <div class="d-flex align-items-center h-100" :style="{ paddingLeft: (row._depth || 0) * 18 + 'px' }">
                <!-- Раскрытие ветки на месте, как в UTM-отчёте: родитель и дети
                     видны одновременно и сравниваются, никуда уходить не надо. -->
                <template v-if="isUtmBreakdown">
                  <span
                    v-if="canExpand(row)"
                    class="rb-expand-btn"
                    @click="toggleExpand(row)"
                  >
                    <span
                      v-if="isLoading(row)"
                      class="spinner-grow spinner-grow-sm"
                      role="status"
                      style="width: 9px; height: 9px;"
                    ></span>
                    <template v-else>{{ isExpanded(row) ? '−' : '+' }}</template>
                  </span>
                  <span v-else class="rb-expand-spacer"></span>
                </template>
                <span class="mb-0 text-sm fw-bold">{{ rowTitle(row) }}</span>
              </div>
            </td>
            <td class="align-middle text-center text-sm font-weight-bold">
              {{ row.size }}
            </td>
            <td
              v-for="(val, idx) in row.intervals"
              :key="'cell-'+rIdx+'-'+idx"
              class="align-middle text-center text-sm"
              :style="getCellStyle(val)"
            >
              <el-tooltip v-if="val !== null" :content="cellTooltip(row, idx)" placement="top" :show-after="150" :teleported="true">
                <span :class="{ 'font-weight-bold text-white': val > 80 }">
                  {{ val }}%
                </span>
              </el-tooltip>
            </td>
            <!-- Empty cells for incomplete cohorts -->
            <td
              v-for="emptyIdx in (maxIntervals - row.intervals.length)"
              :key="'empty-'+rIdx+'-'+emptyIdx"
              class="align-middle text-center empty-cell"
            >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="text-center py-5 text-muted">
      <i class="fas fa-chart-pie fa-3x mb-3 opacity-50"></i>
      <h5>{{ $t('ReportBuilder.retention.noData') }}</h5>
      <p class="text-sm">{{ $t('ReportBuilder.retention.adjustFilters') }}</p>
    </div>
  </div>
</template>

<script>
import { RETENTION_PALETTE } from "@/helpers/retentionPalette";
import { UTM_PALETTE } from "@/helpers/reports/palette";
import { isRetentionUtmBreakdown, aggregateRetentionByLabel } from "@/helpers/reports/retentionUtm";
import ReportBuilderService from "@/helpers/api/reportBuilder.service";

export default {
  name: "_retention_table",
  data() {
    return {
      palette: RETENTION_PALETTE,
      lineWidths: [{ value: 1 }, { value: 2 }, { value: 3 }],
      // Дерево UTM-меток: какие ветки раскрыты, что уже загружено и что грузится.
      // Состояние локальное — это способ просмотра, а не часть конфигурации отчёта.
      expandedKeys: {},
      // Агрегированные строки ветки — для таблицы; сырые строки бэкенда — для
      // графика (он агрегирует сам, чтобы применить свои настройки среднего).
      childRows: {},
      rawChildRows: {},
      loadingKeys: {},
    }
  },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    },
    // В изолированном режиме (виджет дашборда) запрос ветки должен строиться из
    // конфига этого виджета, а не из глобального состояния конструктора.
    injectedBuildPayload: {
      from: 'injectedBuildPayload',
      default: null
    }
  },
  watch: {
    // Новый прогон отчёта обнуляет дерево: id уровней относятся к прежнему
    // запросу, и раскрытые ветки после смены периода или фильтров врали бы.
    reportData() {
      this.collapseAll();
    }
  },
  computed: {
    reportData() {
      const data = this.injectedReportData ? this.injectedReportData.chartData : this.$store.state.ReportFromBuilder.chartData;
      return data || [];
    },
    hiddenDatasets() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.hiddenDatasets || []
    },
    retentionColors() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.visual?.retention_colors || {}
    },
    retentionCohortStyles() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.visual?.retention_cohort_styles || {}
    },
    granularity() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.source_request.granularity || 'day';
    },
    maxIntervals() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.source_request.retention_total_intervals || 8;
    },
    sourceRequest() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return data.source_request || {};
    },
    isUtmBreakdown() {
      const data = this.injectedReportData || this.$store.state.ReportFromBuilder;
      return isRetentionUtmBreakdown(data);
    },
    /**
     * Плоский список строк для отрисовки: верхний уровень, а под каждой раскрытой
     * меткой — её дети с отступом. `_paletteIndex` сквозной, чтобы цвет строки
     * совпадал с цветом линии на графике (там наборы идут в том же порядке).
     */
    displayRows() {
      // Без разбивки дерева нет — строки когорт как были, но `_key` нужен всё
      // равно: он идёт в :key списка, и без него все строки получили бы undefined.
      if (!this.isUtmBreakdown) {
        return this.processedData.map((row, i) => ({ ...row, _key: row.date || i, _depth: 0 }));
      }

      const rows = [];
      let paletteIndex = 0;

      const walk = (list, depth) => {
        list.forEach(row => {
          rows.push({ ...row, _key: row.key, _depth: depth, _paletteIndex: paletteIndex++ });

          if (this.expandedKeys[row.key] && this.childRows[row.key]) {
            walk(this.childRows[row.key], depth + 1);
          }
        });
      };

      walk(this.processedData, 0);

      return rows;
    },
    processedData() {
      if (!this.reportData || this.reportData.length === 0) return [];

      // В режиме UTM-разбивки строка — это метка, а не когорта: когорты внутри
      // метки уже свёрнуты в одну усреднённую кривую (тем же хелпером, что и на
      // графике, иначе таблица и линии разошлись бы в цифрах).
      if (this.isUtmBreakdown) {
        return aggregateRetentionByLabel(this.reportData, {
          maxIntervals: this.maxIntervals,
          mode: this.sourceRequest.mean_retention_calculation || 'simple',
        });
      }

      let rows = [];

      // `counts` — абсолютные числа вернувшихся по интервалам. Проценты сами по
      // себе скрывают размер выборки, поэтому в подсказке ячейки показываем
      // «вернулось N из M». Для формы данных без счётчиков (`item.intervals`)
      // их просто нет — подсказка тогда ограничивается процентом.
      this.reportData.forEach(item => {
        if (item.date && item.values && Array.isArray(item.values)) {
          const counts = item.values.map(v => v.count);
          const size = counts[0] || 0;
          const intervals = counts.map(c => size > 0 ? Number(((c / size) * 100).toFixed(1)) : 0);
          rows.push({ date: item.date, label: item.label, size, intervals, counts });
        } else if (item.date && item.size !== undefined && item.intervals) {
          rows.push({ date: item.date, size: item.size, intervals: item.intervals, counts: null });
        } else if (item.date && item.totals) {
          const size = item.totals[0] || 0;
          const intervals = item.totals.map(t => size > 0 ? Number(((t / size) * 100).toFixed(1)) : 0);
          rows.push({ date: item.date, size, intervals, counts: item.totals });
        }
      });

      return rows;
    },
    meanRow() {
      if (this.processedData.length === 0) return null;

      const totalSize = this.processedData.reduce((acc, row) => acc + row.size, 0);
      const meanIntervals = [];

      // Абсолютные числа средней строки — это сумма по всем строкам, а не среднее:
      // «вернулось N из M пользователей за период» читается однозначно, среднее
      // арифметическое от счётчиков когорт — нет.
      const hasCounts = this.processedData.every(row => Array.isArray(row.counts));
      const meanCounts = hasCounts ? new Array(this.maxIntervals).fill(0) : null;

      for (let i = 0; i < this.maxIntervals; i++) {
        let sumPercent = 0, count = 0;
        this.processedData.forEach(row => {
          if (row.intervals.length > i && row.intervals[i] !== null) {
            sumPercent += row.intervals[i];
            count++;
          }
          if (meanCounts && row.counts[i] !== undefined) {
            meanCounts[i] += row.counts[i];
          }
        });
        meanIntervals.push(count > 0 ? Number((sumPercent / count).toFixed(1)) : null);
      }

      return {
        counts: meanCounts,
        // Знаменатель для подсказки — всегда полная база периода. В колонке
        // «Размер» без разбивки стоит средняя когорта, и складывать её с суммой
        // вернувшихся нельзя: получилось бы «вернулось 500 из 100».
        countsSize: totalSize,
        // В разбивке строки — метки, и «размер» средней строки это вся база
        // периода, а не средняя когорта: сумма по меткам и есть общее число
        // пользователей (у каждого ровно одна метка, см. argMin в бэкенде).
        size: this.isUtmBreakdown ? totalSize : Math.round(totalSize / this.processedData.length),
        intervals: meanIntervals
      };
    }
  },
  methods: {
    toggleVisibility(index) {
      this.$store.commit('ReportFromBuilder/toggleDatasetVisibility', index)
    },
    getCohortColor(row, index) {
      // Ключ цвета — метка (row.key), а не дата когорты: строки разных меток
      // могут делить одну дату, и цвета бы налезали друг на друга.
      if (this.isUtmBreakdown) {
        return this.retentionColors[row.key] || UTM_PALETTE[index % UTM_PALETTE.length]
      }
      if (row.date && this.retentionColors[row.date]) {
        return this.retentionColors[row.date]
      }
      return this.palette[(index + 1) % this.palette.length]
    },
    /**
     * Подсказка ячейки: сколько пользователей стоит за процентом.
     *
     * Процент без размера выборки читается неверно — 25% от четырёх человек и от
     * четырёх тысяч выглядят одинаково. Если абсолютных чисел в данных нет
     * (форма ответа без счётчиков), показываем только процент, а не выдумываем их.
     */
    cellTooltip(row, idx) {
      const percent = row.intervals[idx]
      const returned = Array.isArray(row.counts) ? row.counts[idx] : null

      if (returned === null || returned === undefined) {
        return `${percent}%`
      }

      return this.$t('ReportBuilder.retention.cellTooltip', {
        returned: Math.round(returned).toLocaleString(),
        size: Math.round(row.countsSize ?? row.size).toLocaleString(),
        percent,
      })
    },
    /** Ключ строки в visual.retention_cohort_styles: метка либо дата когорты. */
    styleKey(row) {
      return this.isUtmBreakdown ? row.key : row.date
    },
    rowTitle(row) {
      if (this.isUtmBreakdown) {
        return row.utm_name || this.$t('ReportBuilder.retention.noUtm')
      }
      return this.formatCohortDate(row)
    },
    /** Есть ли внутри метки следующий уровень иерархии. */
    canExpand(row) {
      return Number(row.next_level) > 0 && Array.isArray(row.current_level) && row.current_level.length > 0
    },
    isExpanded(row) {
      return !!this.expandedKeys[row.key]
    },
    isLoading(row) {
      return !!this.loadingKeys[row.key]
    },
    /**
     * Раскрывает ветку: догружает следующий уровень тем же отчётом с
     * `drill_path` до этой метки и вставляет его строки под родителем.
     */
    async toggleExpand(row) {
      const key = row.key

      if (this.expandedKeys[key]) {
        this.collapse(key)
        return
      }

      if (this.childRows[key]) {
        this.expandedKeys = { ...this.expandedKeys, [key]: true }
        this.setExpandedRows(key, this.rawChildRows[key] || [])
        return
      }

      this.loadingKeys = { ...this.loadingKeys, [key]: true }

      try {
        const payload = this.injectedBuildPayload
          ? this.injectedBuildPayload()
          : JSON.parse(JSON.stringify(this.$store.getters['ReportFromBuilder/reportConfig']))

        if (!payload.source_request) payload.source_request = {}
        payload.source_request.drill_path = [...row.current_level]

        // `$current_resource` резолвится клиентом из текущего бота — в ad-hoc
        // запросе подставить его некому (см. reference_resource_key_filter).
        const rKeys = payload.settings?.params?.[':resource_key']
        if (Array.isArray(rKeys)) {
          const currentBotKey = this.$store.state.init?.current_bot?.key
          payload.settings.params[':resource_key'] = rKeys
            .map(k => (k === '$current_resource' ? currentBotKey : k))
            .filter(Boolean)
        }

        const response = await ReportBuilderService.useReportTemplate({}, payload)
        const rawRows = Array.isArray(response) ? response : (response?.data || [])

        this.rawChildRows = { ...this.rawChildRows, [key]: rawRows }
        this.childRows = {
          ...this.childRows,
          [key]: aggregateRetentionByLabel(rawRows, {
            maxIntervals: this.maxIntervals,
            mode: this.sourceRequest.mean_retention_calculation || 'simple',
          }),
        }
        this.expandedKeys = { ...this.expandedKeys, [key]: true }

        // Новые линии приходят выключенными: раскрытие ветки не должно менять
        // то, что пользователь уже отобрал на графике.
        this.setExpandedRows(key, rawRows)
        const newlyHidden = this.childRows[key]
          .map(child => child.key)
          .filter(k => !this.hiddenDatasets.includes(k))
        if (newlyHidden.length) {
          newlyHidden.forEach(k => this.toggleVisibility(k))
        }
      } catch (e) {
        console.error('Retention UTM drill-down error:', e)
      } finally {
        delete this.loadingKeys[key]
        this.loadingKeys = { ...this.loadingKeys }
      }
    },
    collapse(key) {
      const nextExpanded = { ...this.expandedKeys }
      delete nextExpanded[key]
      this.expandedKeys = nextExpanded
      this.removeExpandedRows(key)

      // Свёрнутая ветка уносит с графика и своих потомков — иначе внуки остались
      // бы линиями без видимой строки в таблице.
      Object.keys(this.expandedKeys)
        .filter(k => k.startsWith(key + '_'))
        .forEach(k => this.collapse(k))
    },
    collapseAll() {
      this.expandedKeys = {}
      this.childRows = {}
      this.rawChildRows = {}
      this.loadingKeys = {}
      this.resetExpandedRows()
    },
    // Строки раскрытых веток для графика лежат в Vuex у конструктора и в
    // локальном состоянии у виджета дашборда — пишем туда, что сейчас в ходу.
    setExpandedRows(key, rows) {
      if (this.injectedReportData) {
        this.injectedReportData.utmExpandedRows = {
          ...(this.injectedReportData.utmExpandedRows || {}), [key]: rows
        }
      } else {
        this.$store.commit('ReportFromBuilder/setUtmExpandedRows', { key, rows })
      }
    },
    removeExpandedRows(key) {
      if (this.injectedReportData) {
        const next = { ...(this.injectedReportData.utmExpandedRows || {}) }
        delete next[key]
        this.injectedReportData.utmExpandedRows = next
      } else {
        this.$store.commit('ReportFromBuilder/removeUtmExpandedRows', key)
      }
    },
    resetExpandedRows() {
      if (this.injectedReportData) {
        this.injectedReportData.utmExpandedRows = {}
      } else {
        this.$store.commit('ReportFromBuilder/resetUtmExpandedRows')
      }
    },
    /** Ключ видимости: метка в дереве, порядковый индекс в обычном ретеншене. */
    rowVisibilityKey(row, index) {
      return this.isUtmBreakdown ? row.key : index + 1
    },
    meanVisibilityKey() {
      return this.isUtmBreakdown ? '__mean__' : 0
    },
    getCohortLineStyle(key) {
      const s = this.retentionCohortStyles[key] || {}
      return {
        lineWidth: s.lineWidth ?? 1,
        lineStyle: s.lineStyle ?? 'solid',
        lineTension: s.lineTension ?? 'smooth',
        showPoints: s.showPoints ?? false,
      }
    },
    updateColor(row, color) {
      // Ключ в retention_colors — метка в режиме разбивки, дата когорты иначе;
      // мутация принимает его как `date` по историческому имени параметра.
      const key = this.isUtmBreakdown ? row.key : row.date
      if (!key) return
      this.$store.commit('ReportFromBuilder/updateRetentionColor', { date: key, color })
    },
    updateCohortStyle(key, patch) {
      this.$store.commit('ReportFromBuilder/updateRetentionCohortStyle', { key, patch })
    },
    formatCohortDate(row) {
      if (!row.date) return row.label || '';
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return row.label || row.date;
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
    },
    getCellStyle(value) {
      if (value === null || value === undefined) return { backgroundColor: 'transparent' };
      if (value === 100) return { backgroundColor: '#409EFF', color: '#fff' };
      if (value === 0) return { backgroundColor: 'rgba(64, 158, 255, 0.02)', color: '#909399' };
      const opacity = 0.1 + (value / 100) * 0.7;
      return {
        backgroundColor: `rgba(64, 158, 255, ${opacity})`,
        color: value > 50 ? '#fff' : '#303133'
      };
    }
  }
}
</script>

<style scoped>
/* Detailed-data block doesn't need its own card chrome — it already sits under the
   chart, and the border/shadow just doubled up the framing. */
.rb-retention-card {
  border: none;
  box-shadow: none;
  background: transparent;
}

.rb-retention-table {
  border-collapse: separate !important;
  border-spacing: 3px !important;
  font-size: 12px;
}

.rb-retention-table th {
  padding: 6px 10px;
  border-bottom: none !important;
}

.rb-retention-table td {
  padding: 2px 6px;
  border: none !important;
  border-radius: 6px;
  height: 28px;
  min-width: 56px;
  vertical-align: middle;
}

/* Bootstrap's text-sm/text-xxs utility classes set font-size directly on these
   cells, which otherwise wins over the table-level font-size above. */
.rb-retention-table td.text-sm,
.rb-retention-table th.text-xxs {
  font-size: 12px;
}

.empty-cell {
  background-color: rgba(0, 0, 0, 0.02);
  border: 1px dashed rgba(0, 0, 0, 0.05) !important;
}

.mean-row td {
  font-weight: bold;
}

/* Подсветка строки под курсором. Именно inset-накладка, а не background: у ячеек
   матрицы свой инлайновый фон (тепловая карта), и фон строки под ним не виден.
   Цвет — из общих токенов, чтобы на тёмной теме подсветка осветляла. */
.rb-retention-table tbody tr:hover td {
  box-shadow: inset 0 0 0 9999px var(--ui-row-hover-overlay, rgba(12, 13, 20, 0.05));
}

/* Строка-распорка между средней и когортами не должна ловить подсветку */
.rb-retention-table tbody tr[style*="height: 4px"]:hover td {
  box-shadow: none;
}

/* Раскрытие ветки дерева меток — по образцу .btn-expand в _utm_report.vue */
.rb-expand-btn,
.rb-expand-spacer {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rb-expand-btn {
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1;
  color: var(--ui-text-3, #909399);
  background: transparent;
  transition: color 0.15s, background 0.15s;
}

.rb-expand-btn:hover {
  color: var(--ui-accent, #5e72e4);
  background: var(--ui-bg-sunken, #f3f4f6);
}

.series-color-indicator {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.series-color-indicator.cursor-pointer:hover {
  transform: scale(1.1);
}

/* Style picker (matches _trends_table) */
.style-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}

.style-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.style-label {
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.style-btn-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.style-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 28px;
  border: 1px solid var(--ui-border, #dcdfe6);
  border-radius: 4px;
  background: var(--ui-bg-card, #fff);
  cursor: pointer;
  color: var(--ui-text-2, #606266);
  padding: 0;
  transition: all 0.15s;
}

.style-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.style-btn.active {
  border-color: #409eff;
  background: #ecf5ff;
  color: #409eff;
}

.color-palette-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.color-option {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: #303133;
  box-shadow: 0 0 4px rgba(0,0,0,0.2);
}
</style>
